import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Lock } from "lucide-react";

// Gate for parent-only actions on a shared device (e.g. opening Settings).
// Verifies the family PIN server-side, no member context needed.
export default function FamilyPinDialog({ title = "Parent only", subtitle = "Enter the family PIN", onCancel, onSuccess }) {
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setChecking(true);
    try {
      const res = await api.verifyFamilyPin(pin);
      if (res.ok) {
        onSuccess();
      } else {
        toast.error("Wrong PIN");
        setPin("");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Wrong PIN");
      else if (status === 429) toast.error("Too many attempts. Try again soon.");
      else toast.error("Something went wrong");
      setPin("");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20,20,18,0.45)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="family-pin-dialog"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-[color:var(--surface)] rounded-xl w-full max-w-sm border border-[color:var(--line)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-[color:var(--line-soft)]">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--bg-soft)]">
            <Lock className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-tiny">{subtitle}</div>
            <h2 className="font-display text-xl tracking-tight">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="ff-btn h-9 w-9 !p-0"
            data-testid="family-pin-cancel"
            aria-label="Cancel"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="p-6">
          <input
            autoFocus
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={12}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••"
            className="w-full border border-[color:var(--line)] rounded-xl px-4 py-4 text-center font-mono text-3xl tracking-[0.5em] focus:outline-none focus:border-[color:var(--ink)]"
            data-testid="family-pin-input"
          />
          <button
            type="submit"
            disabled={checking || !pin}
            className="ff-btn ff-btn-primary w-full mt-4 disabled:opacity-60"
            data-testid="family-pin-submit"
          >
            {checking ? "Checking…" : "Continue"}
          </button>
          <div className="label-tiny text-[color:var(--ink-3)] mt-3 text-center">
            The family PIN keeps curious little hands out of parent-only areas.
          </div>
        </div>
      </form>
    </div>
  );
}
